import * as React from 'react'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'

import BodyText from 'LuxKit/Typography/BodyText'
import InputText from 'LuxKit/Typography/InputText'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import { MAX_TRIP_NAME_CHARS } from 'tripPlanner/config'
import { focusRecentItemRenameInput } from './focusRecentItemRenameInput'

export type RecentItemInlineTitleEditHandle = {
  focus: () => void
}

const RENAME_BLUR_SUPPRESS_MS = 500

const TitleField = styled(TextInputOnly)`
  inline-size: 100%;
  min-inline-size: 0;

  input {
    font-weight: ${props => props.theme.font.primary.weight.superBold};
    padding-block: ${rem(8)};
    padding-inline: ${rem(12)};
    font-size: ${rem(16)};
    line-height: ${rem(24)};
  }

  &.weight-normal input {
    font-weight: ${props => props.theme.font.primary.weight.regular};
  }
`

const ErrorText = styled(InputText)`
  margin-block-start: ${rem(2)};
`

interface Props {
  title?: string
  fallbackTitle: string
  isEditing: boolean
  weight?: 'normal' | 'bold'
  onEditingChange: (isEditing: boolean) => void
  onRename: (name: string) => Promise<void>
}

const RecentItemInlineTitleEdit = forwardRef<RecentItemInlineTitleEditHandle, Props>(function RecentItemInlineTitleEdit({
  title,
  fallbackTitle,
  isEditing,
  weight = 'bold',
  onEditingChange,
  onRename,
}, ref) {
  const intl = useIntl()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [displayTitle, setDisplayTitle] = useState(title?.trim() || fallbackTitle)
  const [draftTitle, setDraftTitle] = useState(displayTitle)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [isSaving, setIsSaving] = useState(false)
  const isSavingRef = useRef(false)
  const suppressBlurUntilRef = useRef(0)
  const endEditingTimeoutRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    const nextTitle = title?.trim() || fallbackTitle
    setDisplayTitle(nextTitle)
    setDraftTitle(nextTitle)
    setErrorMessage(undefined)
  }, [fallbackTitle, title])

  useEffect(() => {
    if (isEditing) {
      setDraftTitle(displayTitle)
      setErrorMessage(undefined)
      suppressBlurUntilRef.current = Date.now() + RENAME_BLUR_SUPPRESS_MS
    }
  }, [displayTitle, isEditing])

  useEffect(() => () => {
    if (endEditingTimeoutRef.current !== undefined) {
      window.clearTimeout(endEditingTimeoutRef.current)
    }
  }, [])

  const focusInput = useCallback(() => {
    suppressBlurUntilRef.current = Date.now() + RENAME_BLUR_SUPPRESS_MS
    if (inputRef.current) {
      focusRecentItemRenameInput(inputRef.current)
    }
  }, [])

  useImperativeHandle(ref, () => ({
    focus: focusInput,
  }), [focusInput])

  const endEditing = useCallback(() => {
    if (endEditingTimeoutRef.current !== undefined) {
      window.clearTimeout(endEditingTimeoutRef.current)
    }
    endEditingTimeoutRef.current = window.setTimeout(() => {
      endEditingTimeoutRef.current = undefined
      onEditingChange(false)
    }, 0)
  }, [onEditingChange])

  const emptyNameError = intl.formatMessage({
    id: 'O3TfpOALSbrat4zK4YFl',
    defaultMessage: 'Name cannot be empty',
    description: 'Inline validation message when a recent trip planner chat or trip rename is empty',
  })

  const renameFailedError = intl.formatMessage({
    id: 'svK_yN91_Xg6AenKk3hb',
    defaultMessage: 'Unable to rename. Please try again.',
    description: 'Inline validation message when renaming a recent trip planner chat or trip fails',
  })

  const renameInputLabel = intl.formatMessage({
    id: 'j3PZWc-b5voroPmGdGIW',
    defaultMessage: 'Rename',
    description: 'Accessible label for the inline rename input on recent trip planner items',
  })

  const commitRename = useCallback(async() => {
    if (isSavingRef.current) {
      return
    }

    const trimmedTitle = draftTitle.trim()
    if (!trimmedTitle) {
      setErrorMessage(emptyNameError)
      focusInput()
      return
    }

    if (trimmedTitle === displayTitle) {
      endEditing()
      return
    }

    isSavingRef.current = true
    setIsSaving(true)
    try {
      await onRename(trimmedTitle)
      setDisplayTitle(trimmedTitle)
      endEditing()
    } catch {
      setErrorMessage(renameFailedError)
      focusInput()
    } finally {
      isSavingRef.current = false
      setIsSaving(false)
    }
  }, [displayTitle, draftTitle, emptyNameError, endEditing, focusInput, onRename, renameFailedError])

  const cancelRename = useCallback(() => {
    setDraftTitle(displayTitle)
    setErrorMessage(undefined)
    endEditing()
  }, [displayTitle, endEditing])

  const handleBlur = useCallback(() => {
    if (
      Date.now() < suppressBlurUntilRef.current &&
      draftTitle.trim() === displayTitle
    ) {
      inputRef.current?.focus()
      return
    }
    commitRename().catch(() => undefined)
  }, [commitRename, displayTitle, draftTitle])

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setDraftTitle(event.currentTarget.value)
    if (errorMessage) {
      setErrorMessage(undefined)
    }
  }, [errorMessage])

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      commitRename().catch(() => undefined)
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      cancelRename()
    }
  }, [cancelRename, commitRename])

  const stopNavigationGesture = useCallback((event: React.SyntheticEvent) => {
    if (event.type === 'click') {
      event.preventDefault()
    }
    event.stopPropagation()
  }, [])

  if (!isEditing) {
    return (
      <BodyText variant="medium" weight={weight} align="start" lineClamp={1}>
        {displayTitle}
      </BodyText>
    )
  }

  return (
    <>
      <TitleField
        ref={inputRef}
        aria-label={renameInputLabel}
        value={draftTitle}
        maxLength={MAX_TRIP_NAME_CHARS}
        disabled={isSaving}
        className={`weight-${weight}`}
        noValidationSpacing
        noValidationMessage
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={stopNavigationGesture}
        onMouseDown={stopNavigationGesture}
        onPointerDown={stopNavigationGesture}
        onTouchStart={stopNavigationGesture}
      />
      {!!errorMessage && (
        <ErrorText variant="caption" colour="critical" role="alert">
          {errorMessage}
        </ErrorText>
      )}
    </>
  )
})

export default RecentItemInlineTitleEdit
